import { useState } from "react";
import { api } from "../lib/api";
import { Sparkles, Loader2, FileSearch, Users2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AIAssistant() {
  const [tab, setTab] = useState("tdr");

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Intelligence augmentée</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Sparkles className="text-btp-terra" /> Assistant IA
        </h1>
        <p className="text-sm text-slate-500 mt-1">Powered by GPT-5.2 — analyse TDR, suggestion d'équipe, détection de risques</p>
      </div>

      <div className="border-b border-slate-200 flex gap-1">
        <TabBtn active={tab === "tdr"} onClick={() => setTab("tdr")} icon={FileSearch} testid="ai-tab-tdr">Analyse TDR</TabBtn>
        <TabBtn active={tab === "team"} onClick={() => setTab("team")} icon={Users2} testid="ai-tab-team">Suggestion équipe</TabBtn>
        <TabBtn active={tab === "risks"} onClick={() => setTab("risks")} icon={AlertTriangle} testid="ai-tab-risks">Détection risques</TabBtn>
      </div>

      {tab === "tdr" && <AnalyzeTDR />}
      {tab === "team" && <SuggestTeam />}
      {tab === "risks" && <DetectRisks />}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${active ? "text-btp-navy border-btp-navy" : "text-slate-500 border-transparent hover:text-slate-700"}`}>
      <Icon size={15} /> {children}
    </button>
  );
}

function AnalyzeTDR() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/analyze-tdr", { tdr_text: text });
      setResult(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Coller le texte du TDR</div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
          placeholder="Collez ici le contenu intégral du Terme de Référence (mission, objectifs, livrables, durée…)"
          className="w-full border border-slate-300 rounded p-3 text-sm font-mono focus:border-btp-navy outline-none"
          data-testid="ai-tdr-input" />
        <button onClick={submit} disabled={loading || text.length < 30} className="mt-3 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-50 text-white px-4 h-10 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="ai-tdr-submit">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Analyser le TDR
        </button>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4 fade-in" data-testid="ai-tdr-result">
          {result.error ? <div className="text-red-600 text-sm">Erreur: {result.error}</div> : (
            <>
              <Section title="Mission">
                <div className="font-heading text-lg font-bold">{result.titre_mission}</div>
                <div className="text-sm text-slate-600">{result.client}</div>
                <div className="text-sm leading-relaxed mt-2">{result.resume}</div>
              </Section>
              <Section title="Objectifs">
                <ul className="list-disc pl-5 text-sm space-y-1">{(result.objectifs || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
              </Section>
              <Section title="Livrables attendus">
                <ul className="list-disc pl-5 text-sm space-y-1">{(result.livrables_attendus || []).map((o, i) => <li key={i}>{o}</li>)}</ul>
              </Section>
              <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded overflow-hidden">
                <Stat label="Durée estimée" value={`${result.duree_estimee_jours || "?"} j`} />
                <Stat label="Phases" value={result.phases?.length || 0} />
                <Stat label="Complexité" value={(result.complexite || "—").toUpperCase()} />
              </div>
              <Section title="Compétences requises">
                <div className="flex flex-wrap gap-1.5">{(result.competences_requises || []).map((c, i) => <span key={i} className="text-xs px-2 py-1 bg-slate-100 rounded">{c}</span>)}</div>
              </Section>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestTeam() {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/suggest-team", { tdr_summary: summary });
      setResult(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Résumé de la mission</div>
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={6}
          placeholder="Décrivez la mission (type, périmètre, durée…)"
          className="w-full border border-slate-300 rounded p-3 text-sm focus:border-btp-navy outline-none" data-testid="ai-team-input" />
        <button onClick={submit} disabled={loading || summary.length < 20} className="mt-3 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-50 text-white px-4 h-10 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="ai-team-submit">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Composer l'équipe
        </button>
      </div>
      {result && !result.error && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4 fade-in" data-testid="ai-team-result">
          <Section title="Équipe recommandée">
            <div className="space-y-2">
              {(result.equipe_recommandee || []).map((m, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded">
                  <div>
                    <div className="font-medium text-sm">{m.nom}</div>
                    <div className="text-xs text-slate-500">{m.role_mission}</div>
                  </div>
                  <div className="text-xs text-slate-600 max-w-md text-right">{m.justification}</div>
                </div>
              ))}
            </div>
          </Section>
          {result.profils_manquants?.length > 0 && (
            <Section title="Profils manquants à recruter">
              <div className="flex flex-wrap gap-1.5">{result.profils_manquants.map((p, i) => <span key={i} className="text-xs px-2 py-1 bg-amber-100 text-amber-900 rounded">{p}</span>)}</div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function DetectRisks() {
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/detect-risks", { project_summary: summary });
      setResult(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const impactColor = (v) => ({ faible: "bg-emerald-100 text-emerald-900", moyen: "bg-amber-100 text-amber-900", élevé: "bg-orange-100 text-orange-900", critique: "bg-red-100 text-red-900" }[v] || "bg-slate-100");
  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Description du projet</div>
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={6}
          placeholder="Décrivez le projet et son contexte (zone, durée, contraintes…)"
          className="w-full border border-slate-300 rounded p-3 text-sm focus:border-btp-navy outline-none" data-testid="ai-risks-input" />
        <button onClick={submit} disabled={loading || summary.length < 20} className="mt-3 bg-btp-terra hover:bg-btp-terra-hover disabled:opacity-50 text-white px-4 h-10 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="ai-risks-submit">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />} Identifier les risques
        </button>
      </div>
      {result && !result.error && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4 fade-in" data-testid="ai-risks-result">
          <div className="flex items-center justify-between">
            <Section title="Score de risque global">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${impactColor(result.score_risque_global)}`}>{(result.score_risque_global || "").toUpperCase()}</span>
            </Section>
          </div>
          <Section title={`Risques identifiés (${result.risques?.length || 0})`}>
            <div className="space-y-2">
              {(result.risques || []).map((r, i) => (
                <div key={i} className="border border-slate-200 rounded p-3">
                  <div className="flex justify-between gap-2 items-start">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{r.categorie}</div>
                      <div className="text-sm font-medium mt-0.5">{r.description}</div>
                    </div>
                    <div className="flex gap-1.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${impactColor(r.impact)}`}>impact: {r.impact}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100">prob: {r.probabilite}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 mt-2"><strong>Mitigation:</strong> {r.mitigation}</div>
                </div>
              ))}
            </div>
          </Section>
          {result.recommandations?.length > 0 && (
            <Section title="Recommandations">
              <ul className="list-disc pl-5 text-sm space-y-1">{result.recommandations.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="font-heading text-base font-bold mt-0.5">{value}</div>
    </div>
  );
}
